import { DestroyRef, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';

import { CalendarDay, isCalendarDay } from '@shared/models/dateTypes';

/**
 * How one filter travels through the URL. `read` gets the whole query, so a filter may span several
 * params (a period is `fra` + `til`); it returns `undefined` when the URL says nothing, and the
 * filter then falls back to its default. `write` returns every param the filter owns — `null`
 * removes one.
 */
export interface FilterCodec<T> {
  read(params: ParamMap): T | undefined;
  write(value: T): Params;
  /** The params this filter owns, cleared when the value is back at its default. */
  readonly params: readonly string[];
  equals(a: T, b: T): boolean;
}

/** A period on a list page. Either end may be open. */
export interface PeriodValue {
  from: CalendarDay | null;
  to: CalendarDay | null;
}

export interface FilterDef<T> {
  codec: FilterCodec<T>;
  /** The page's own standard (R8). A function runs once, in the page's injection context. */
  defaultValue: T | (() => T);
}

type Defs = Record<string, FilterDef<unknown>>;
export type FilterValues<D extends Defs> = {
  [K in keyof D]: D[K] extends FilterDef<infer T> ? T : never;
};

// --- codecs ------------------------------------------------------------------

/** Same members, in any order — "Anlæg 2, 1" is the same filter as "Anlæg 1, 2". */
const sameArray = <T>(a: readonly T[], b: readonly T[]) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((v) => set.has(v));
};

/** Free text, `?q=…`. Trimmed on the way in; empty is absent. */
export function textFilter(param = 'q', defaultValue = ''): FilterDef<string> {
  return {
    defaultValue,
    codec: {
      params: [param],
      read: (p) => (p.has(param) ? (p.get(param) ?? '').trim() : undefined),
      write: (v) => ({ [param]: v.trim() || null }),
      equals: (a, b) => a.trim() === b.trim(),
    },
  };
}

/** One value out of a list, `?status=3`. `null` is "Alle". */
export function singleFilter<T extends string | number>(
  param: string,
  defaultValue: T | null | (() => T | null) = null,
  type: 'number' | 'string' = 'number',
): FilterDef<T | null> {
  const parse = (raw: string) => (type === 'number' ? Number(raw) : raw) as T;
  return {
    defaultValue,
    codec: {
      params: [param],
      read: (p) => {
        if (!p.has(param)) return undefined;
        const raw = p.get(param) ?? '';
        // `?status=` is an explicit "Alle" — it differs from absent when the default is a value.
        if (raw === '') return null;
        const value = parse(raw);
        return type === 'number' && Number.isNaN(value) ? undefined : value;
      },
      write: (v) => ({ [param]: v === null ? '' : String(v) }),
      equals: (a, b) => a === b,
    },
  };
}

/**
 * Several values, `?anlaeg=1&anlaeg=2` (R7). The empty list is "Alle". When the page's standard is
 * a non-empty list, a chosen "Alle" is written as `?anlaeg=` so a reload keeps it (R8).
 *
 * `joined` writes one comma list instead, `?location_id=1,4` — for params other pages already
 * read that way. Either form is read.
 */
export function multiFilter<T extends string | number>(
  param: string,
  defaultValue: readonly T[] | (() => readonly T[]) = [],
  type: 'number' | 'string' = 'number',
  { joined = false }: { joined?: boolean } = {},
): FilterDef<readonly T[]> {
  const parse = (raw: string) => (type === 'number' ? Number(raw) : raw) as T;
  return {
    defaultValue,
    codec: {
      params: [param],
      read: (p) => {
        if (!p.has(param)) return undefined;
        const values = p
          .getAll(param)
          .flatMap((raw) => raw.split(','))
          .filter((raw) => raw !== '')
          .map(parse)
          .filter((v) => !(type === 'number' && Number.isNaN(v)));
        return [...new Set(values)];
      },
      write: (v) => ({ [param]: v.length ? (joined ? v.join(',') : v.map(String)) : '' }),
      equals: sameArray,
    },
  };
}

/** An on/off chip, `?ikkegodkendt=1`. */
export function toggleFilter(param: string, defaultValue = false): FilterDef<boolean> {
  return {
    defaultValue,
    codec: {
      params: [param],
      read: (p) => (p.has(param) ? p.get(param) === '1' : undefined),
      write: (v) => ({ [param]: v ? '1' : '0' }),
      equals: (a, b) => a === b,
    },
  };
}

/** A period, `?fra=2026-10-01&til=2026-10-31`. A malformed day is ignored, not trusted. */
export function periodFilter(
  defaultValue: PeriodValue | (() => PeriodValue) = { from: null, to: null },
  params: { from: string; to: string } = { from: 'fra', to: 'til' },
): FilterDef<PeriodValue> {
  const day = (p: ParamMap, name: string) => {
    const raw = p.get(name);
    return raw && isCalendarDay(raw) ? raw : null;
  };
  return {
    defaultValue,
    codec: {
      params: [params.from, params.to],
      read: (p) =>
        p.has(params.from) || p.has(params.to)
          ? { from: day(p, params.from), to: day(p, params.to) }
          : undefined,
      // An open end is written as empty, so "no start" survives a reload when the default has one.
      write: (v) => ({ [params.from]: v.from ?? '', [params.to]: v.to ?? '' }),
      equals: (a, b) => a.from === b.from && a.to === b.to,
    },
  };
}

// --- state -------------------------------------------------------------------

export interface FilterState<D extends Defs> {
  /** Every filter's current value. */
  readonly values: Signal<FilterValues<D>>;
  /** One filter's current value. */
  value<K extends keyof D>(key: K): Signal<FilterValues<D>[K]>;
  /** The page's standard for one filter. */
  defaultOf<K extends keyof D>(key: K): FilterValues<D>[K];
  /** Whether a filter differs from the page's standard (R4/R8). */
  isChanged<K extends keyof D>(key: K): Signal<boolean>;
  readonly changedCount: Signal<number>;
  readonly anyChanged: Signal<boolean>;
  set<K extends keyof D>(key: K, value: FilterValues<D>[K]): void;
  /** Several filters in one navigation — e.g. Anlæg and the Aktivitet that depends on it. */
  patch(values: Partial<FilterValues<D>>): void;
  /**
   * Filters back to the page's standard — the chip's ×. Several in one navigation for a chip that
   * stands for more than one, e.g. Periode with its date type (#1074).
   */
  reset<K extends keyof D>(key: K, ...more: K[]): void;
  /** Every filter back to the page's standard — "Nulstil filtre". */
  resetAll(): void;
  /** The filters as query params, for a link that carries them along (list ↔ kalender, R9). */
  readonly queryParams: Signal<Params>;
  /** One line explaining a filter that changed by itself (R11). Shown in the band, closable. */
  readonly notice: Signal<string | null>;
  /** Resets dependent filters and says why. */
  resetWithNotice(keys: (keyof D)[], message: string): void;
  dismissNotice(): void;
}

/**
 * The filters of one list page, kept in the URL (R9). Replaces each page's own
 * `route.snapshot.queryParams` + `router.navigate`.
 *
 * Call it in a field initialiser or constructor — it injects the router and follows the URL, so
 * back/forward and a pasted link update the filters. A value equal to the page's standard is left
 * out of the URL; anything else, including a chosen "Alle", is written (R8).
 *
 * ```ts
 * readonly filters = createFilterState({
 *   q: textFilter(),
 *   anlaeg: multiFilter<number>('anlaeg'),
 *   periode: periodFilter(() => ({ from: today, to: today })),
 *   status: singleFilter<number>('status', 3),
 * });
 * ```
 */
export function createFilterState<D extends Defs>(defs: D): FilterState<D> {
  const router = inject(Router);
  const route = inject(ActivatedRoute);
  const destroyRef = inject(DestroyRef);

  const keys = Object.keys(defs) as (keyof D & string)[];
  const defaults = Object.fromEntries(
    keys.map((k) => {
      const d = defs[k].defaultValue;
      return [k, typeof d === 'function' ? (d as () => unknown)() : d];
    }),
  ) as FilterValues<D>;

  // `undefined` is "the URL says nothing"; `null` is a chosen "Alle" and must not fall back (R8).
  const fromParams = (params: ParamMap) =>
    Object.fromEntries(
      keys.map((k) => {
        const value = defs[k].codec.read(params);
        return [k, value === undefined ? defaults[k] : value];
      }),
    ) as FilterValues<D>;

  const state: WritableSignal<FilterValues<D>> = signal(fromParams(route.snapshot.queryParamMap));
  const notice = signal<string | null>(null);

  const changed = (k: keyof D, values: FilterValues<D>) =>
    !defs[k].codec.equals(values[k], defaults[k]);

  // The URL is the source of truth for back/forward and pasted links. Our own navigations come
  // back through here too and land on the value already set.
  const sub = route.queryParamMap.subscribe((params) => {
    const next = fromParams(params);
    if (keys.some((k) => !defs[k].codec.equals(next[k], state()[k]))) state.set(next);
  });
  destroyRef.onDestroy(() => sub.unsubscribe());

  const paramsOf = (values: FilterValues<D>): Params => {
    const out: Params = {};
    for (const k of keys) {
      const codec = defs[k].codec;
      if (changed(k, values)) Object.assign(out, codec.write(values[k]));
      else for (const p of codec.params) out[p] = null;
    }
    return out;
  };

  const commit = (next: FilterValues<D>) => {
    state.set(next);
    void router.navigate([], {
      relativeTo: route,
      queryParams: paramsOf(next),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  };

  const applyReset = (resetKeys: readonly (keyof D)[]) => {
    const next = { ...state() };
    for (const k of resetKeys) next[k] = defaults[k];
    commit(next);
  };

  const valueSignals = new Map<keyof D, Signal<unknown>>();
  const changedSignals = new Map<keyof D, Signal<boolean>>();

  return {
    values: state.asReadonly(),
    value: (key) => {
      if (!valueSignals.has(key))
        valueSignals.set(
          key,
          computed(() => state()[key]),
        );
      return valueSignals.get(key) as Signal<FilterValues<D>[typeof key]>;
    },
    defaultOf: (key) => defaults[key],
    isChanged: (key) => {
      if (!changedSignals.has(key))
        changedSignals.set(
          key,
          computed(() => changed(key, state())),
        );
      return changedSignals.get(key)!;
    },
    changedCount: computed(() => keys.filter((k) => changed(k, state())).length),
    anyChanged: computed(() => keys.some((k) => changed(k, state()))),
    set: (key, value) => commit({ ...state(), [key]: value }),
    patch: (values) => commit({ ...state(), ...values }),
    reset: (key, ...more) => applyReset([key, ...more]),
    resetAll: () => {
      notice.set(null);
      commit({ ...defaults });
    },
    queryParams: computed(() => {
      // Only the filters that say something, without the nulls that clear a param.
      const params = paramsOf(state());
      return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null));
    }),
    notice: notice.asReadonly(),
    resetWithNotice: (resetKeys, message) => {
      notice.set(message);
      applyReset(resetKeys);
    },
    dismissNotice: () => notice.set(null),
  };
}
